import { baseApi } from "@/services/api";
import { store } from "@/app/store";
import type { AppDispatch } from "@/app/store";
import { DELETE_CONFIRM_TOKEN } from "@/features/catalog-lifecycle/catalogLifecycleTypes";
import type {
  CatalogBulkPurgeResponse,
  CatalogPurgePreflight,
} from "@/features/catalog-lifecycle/catalogLifecycleTypes";

// V2 Music Catalog · Albums API client.
//
// Backed by kk-admin-sa-music-catalog (Slice #3) through the custom admin
// domain `/albums` basepath mapping. Reads hit
// kk-music-album-catalog-projection-${stage} (HR-13: UI never reads
// canonical state directly). Mutations patch all cached list queries from
// the canonical mutation response, then briefly poll projection GET until
// the materializer catches up — same posture as artistsApi/recordLabelsApi
// (no LIST-tag invalidation on write; an immediate projection refetch can
// race the stream and revert optimistic rows).
//
// Albums are created two ways, both canonical:
//   * automatically by the processing pipeline via the resolve-or-create
//     seam (createdBySource: "pipeline"), and
//   * manually from this console (createdBySource: "user").
// This page manages/display both — it never builds relationships itself.
//
// Lifecycle (owner 2026-07-25 — Song Library parity):
//   Active → Archive → (Restore | Permanent Delete). Permanent Delete keeps
//   the audit tombstone and hard-deletes the canonical album row.

export type AlbumStatus = "active" | "archived";

export interface Album {
  albumId: string;
  title: string;
  titleLower: string;
  primaryArtistId: string;
  recordLabelId: string;
  releaseDate?: string | null;
  artworkKey?: string | null;
  createdBySource?: string | null;
  sourceProcessingJobId?: string | null;
  /** Active Songs only (authoritative server count). */
  activeSongCount?: number;
  status: AlbumStatus;
  createdAt: string;
  updatedAt: string;
  version: number;
  materializedAt?: string;
  deletedAt?: string | null;
}

export interface ListAlbumsArgs {
  status?: AlbumStatus | "all";
  titleStartsWith?: string;
  limit?: number;
  pageToken?: string;
}

export interface ListAlbumsResponse {
  albums: Album[];
  pageToken: string | null;
  limit: number;
  status: string;
}

export interface CreateAlbumArgs {
  title: string;
  primaryArtistId: string;
  recordLabelId: string;
  releaseDate?: string;
}

export interface UpdateAlbumArgs {
  albumId: string;
  title?: string;
  primaryArtistId?: string;
  recordLabelId?: string;
  releaseDate?: string | null;
}

export interface ArchiveAlbumArgs {
  albumId: string;
}

export interface RestoreAlbumArgs {
  albumId: string;
}

export interface RestoreAlbumResponse {
  albumId: string;
  status: "active";
  restoredAt: string;
  restoredBy?: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  version: number;
}

export interface PurgeAlbumArgs {
  albumId: string;
  reason: string;
  confirmTitle?: string;
  confirmAlbumId?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

const PROJECTION_SYNC_MAX_ATTEMPTS = 12;
const PROJECTION_SYNC_INTERVAL_MS = 400;

function albumTimestampMs(album: Pick<Album, "updatedAt">): number {
  const parsed = Date.parse(album.updatedAt);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Prefer canonical mutation rows over stale projection reads. */
function isAlbumAtLeastAsNew(candidate: Album, existing: Album | undefined): boolean {
  if (!existing) return true;
  if (candidate.version > existing.version) return true;
  if (candidate.version < existing.version) return false;
  return albumTimestampMs(candidate) >= albumTimestampMs(existing);
}

function patchAllAlbumsLists(
  dispatch: AppDispatch,
  mutator: (draft: ListAlbumsResponse, listArgs: ListAlbumsArgs) => void,
) {
  const cachedArgs = albumsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listAlbums",
  );
  for (const args of cachedArgs) {
    const listArgs = (args ?? {}) as ListAlbumsArgs;
    dispatch(
      albumsApi.util.updateQueryData("listAlbums", args, (draft) => {
        mutator(draft, listArgs);
      }),
    );
  }
}

function mergeAlbumPreservingSongCount(existing: Album | undefined, album: Album): Album {
  if (
    typeof album.activeSongCount === "number" ||
    !existing ||
    typeof existing.activeSongCount !== "number"
  ) {
    return album;
  }
  return { ...album, activeSongCount: existing.activeSongCount };
}

function applyAlbumToListCache(
  draft: ListAlbumsResponse,
  album: Album,
  listArgs: ListAlbumsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  const idx = draft.albums.findIndex((row) => row.albumId === album.albumId);
  const existing = idx >= 0 ? draft.albums[idx] : undefined;
  const merged = mergeAlbumPreservingSongCount(existing, album);

  if (merged.status === "archived") {
    if (filterStatus === "active") {
      if (idx >= 0) draft.albums.splice(idx, 1);
      return;
    }
    if (filterStatus === "archived" || filterStatus === "all") {
      if (existing && !isAlbumAtLeastAsNew(merged, existing)) return;
      if (idx >= 0) draft.albums[idx] = merged;
      else draft.albums.unshift(merged);
    }
    return;
  }

  if (filterStatus === "archived") {
    if (idx >= 0) draft.albums.splice(idx, 1);
    return;
  }

  if (existing && !isAlbumAtLeastAsNew(merged, existing)) return;
  if (idx >= 0) draft.albums[idx] = merged;
  else draft.albums.unshift(merged);
}

function removeAlbumFromListCache(
  draft: ListAlbumsResponse,
  albumId: string,
  listArgs: ListAlbumsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.albums.findIndex((row) => row.albumId === albumId);
    if (idx >= 0) {
      draft.albums[idx] = { ...draft.albums[idx], status: "archived" };
    }
    return;
  }
  draft.albums = draft.albums.filter((row) => row.albumId !== albumId);
}

/**
 * Archive → Restore: drop from Archived-only lists, flip status on All.
 * Active list gains the row once scheduleProjectionUpsert lands.
 */
function restoreAlbumInListCache(
  draft: ListAlbumsResponse,
  albumId: string,
  listArgs: ListAlbumsArgs,
) {
  const filterStatus = listArgs.status ?? "active";
  if (filterStatus === "all") {
    const idx = draft.albums.findIndex((row) => row.albumId === albumId);
    if (idx >= 0) {
      draft.albums[idx] = { ...draft.albums[idx], status: "active" };
    }
    return;
  }
  if (filterStatus === "archived") {
    draft.albums = draft.albums.filter((row) => row.albumId !== albumId);
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function scheduleProjectionUpsert(
  dispatch: AppDispatch,
  albumId: string,
  canonical?: Album,
) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            albumsApi.endpoints.getAlbum.initiate(
              { albumId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        if (canonical && !isAlbumAtLeastAsNew(projected, canonical)) {
          continue;
        }
        patchAllAlbumsLists(dispatch, (draft, listArgs) => {
          applyAlbumToListCache(draft, projected, listArgs);
        });
        return;
      } catch {
        // Projection row not visible yet — list cache keeps the canonical patch.
      }
    }
  })();
}

function scheduleProjectionRemoval(dispatch: AppDispatch, albumId: string) {
  void (async () => {
    for (let attempt = 0; attempt < PROJECTION_SYNC_MAX_ATTEMPTS; attempt += 1) {
      await delay(PROJECTION_SYNC_INTERVAL_MS);
      try {
        const projected = await store
          .dispatch(
            albumsApi.endpoints.getAlbum.initiate(
              { albumId },
              { forceRefetch: true, subscribe: false },
            ),
          )
          .unwrap();
        if (projected.status === "archived") {
          patchAllAlbumsLists(dispatch, (draft, listArgs) => {
            removeAlbumFromListCache(draft, albumId, listArgs);
          });
          return;
        }
      } catch {
        patchAllAlbumsLists(dispatch, (draft, listArgs) => {
          removeAlbumFromListCache(draft, albumId, listArgs);
        });
        return;
      }
    }
  })();
}

export const albumsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listAlbums: builder.query<ListAlbumsResponse, ListAlbumsArgs | void>({
      query: (args) => {
        const params: Record<string, string | number> = {};
        if (args && args.status) params.status = args.status;
        if (args && args.titleStartsWith) params.titleStartsWith = args.titleStartsWith;
        if (args && args.limit) params.limit = args.limit;
        if (args && args.pageToken) params.pageToken = args.pageToken;
        return {
          url: "/albums",
          method: "GET",
          params,
        };
      },
      transformResponse: (raw: ApiEnvelope<ListAlbumsResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to load albums");
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.albums
          ? [
              ...result.albums.map((row) => ({
                type: "Albums" as const,
                id: row.albumId,
              })),
              { type: "Albums" as const, id: "LIST" },
            ]
          : [{ type: "Albums" as const, id: "LIST" }],
    }),
    getAlbum: builder.query<Album, { albumId: string; includeArchived?: boolean }>({
      query: ({ albumId, includeArchived }) => ({
        url: `/albums/${encodeURIComponent(albumId)}`,
        method: "GET",
        params: includeArchived ? { includeArchived: "true" } : {},
      }),
      transformResponse: (raw: ApiEnvelope<{ album: Album }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.album) {
          throw new Error(raw && raw.message ? raw.message : "Failed to load album");
        }
        return raw.data.album;
      },
      providesTags: (_result, _error, { albumId }) => [
        { type: "Albums" as const, id: albumId },
      ],
    }),
    createAlbum: builder.mutation<Album, CreateAlbumArgs>({
      query: (body) => ({
        url: "/albums",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ album: Album }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.album) {
          throw new Error(raw && raw.message ? raw.message : "Failed to create album");
        }
        return raw.data.album;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          patchAllAlbumsLists(dispatch, (draft, listArgs) => {
            applyAlbumToListCache(draft, created, listArgs);
          });
          scheduleProjectionUpsert(dispatch, created.albumId, created);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    updateAlbum: builder.mutation<Album, UpdateAlbumArgs>({
      query: ({ albumId, ...patch }) => ({
        url: `/albums/${encodeURIComponent(albumId)}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: (raw: ApiEnvelope<{ album: Album }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.album) {
          throw new Error(raw && raw.message ? raw.message : "Failed to update album");
        }
        return raw.data.album;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          patchAllAlbumsLists(dispatch, (draft, listArgs) => {
            applyAlbumToListCache(draft, updated, listArgs);
          });
          scheduleProjectionUpsert(dispatch, updated.albumId, updated);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    archiveAlbum: builder.mutation<
      { albumId: string; status: AlbumStatus; deletedAt: string; version: number },
      ArchiveAlbumArgs
    >({
      query: ({ albumId }) => ({
        url: `/albums/${encodeURIComponent(albumId)}`,
        method: "DELETE",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          albumId: string;
          status: AlbumStatus;
          deletedAt: string;
          version: number;
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to archive album");
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ albumId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllAlbumsLists(dispatch, (draft, listArgs) => {
            removeAlbumFromListCache(draft, albumId, listArgs);
          });
          scheduleProjectionRemoval(dispatch, albumId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    restoreAlbum: builder.mutation<RestoreAlbumResponse, RestoreAlbumArgs>({
      query: ({ albumId }) => ({
        url: `/albums/${encodeURIComponent(albumId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreAlbumResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to restore album",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ albumId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllAlbumsLists(dispatch, (draft, listArgs) => {
            restoreAlbumInListCache(draft, albumId, listArgs);
          });
          scheduleProjectionUpsert(dispatch, albumId);
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    preflightPurgeAlbum: builder.mutation<
      CatalogPurgePreflight,
      { albumId: string }
    >({
      query: ({ albumId }) => ({
        url: `/albums/${encodeURIComponent(albumId)}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogPurgePreflight>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load album deletion preflight",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
    }),
    purgeAlbum: builder.mutation<
      {
        albumId: string;
        name?: string;
        purged: boolean;
        dependencyCheck?: {
          checkStatus: string;
          associatedTrackCount: number;
          message?: string;
        };
      },
      PurgeAlbumArgs
    >({
      query: ({ albumId, reason, confirmTitle, confirmAlbumId }) => ({
        url: `/albums/${encodeURIComponent(albumId)}/purge`,
        method: "POST",
        body: {
          confirm: DELETE_CONFIRM_TOKEN,
          reason,
          ...(confirmTitle ? { confirmTitle } : {}),
          ...(confirmAlbumId ? { confirmAlbumId } : {}),
        },
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          albumId: string;
          name?: string;
          purged: boolean;
          dependencyCheck?: {
            checkStatus: string;
            associatedTrackCount: number;
            message?: string;
          };
        }>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to purge album",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ albumId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchAllAlbumsLists(dispatch, (draft) => {
            draft.albums = draft.albums.filter((row) => row.albumId !== albumId);
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
    bulkPurgeAlbums: builder.mutation<
      CatalogBulkPurgeResponse,
      { ids: string[]; reason: string }
    >({
      query: ({ ids, reason }) => ({
        url: "/albums/permanent-delete",
        method: "POST",
        body: { ids, confirm: DELETE_CONFIRM_TOKEN, reason },
      }),
      transformResponse: (raw: ApiEnvelope<CatalogBulkPurgeResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to permanently delete albums",
          );
        }
        return raw.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const purgedIds = (data.results || [])
            .filter((row) => row.ok)
            .map((row) => row.id);
          if (purgedIds.length === 0) return;
          patchAllAlbumsLists(dispatch, (draft) => {
            draft.albums = draft.albums.filter(
              (row) => !purgedIds.includes(row.albumId),
            );
          });
        } catch {
          // Mutation failed — leave list cache unchanged.
        }
      },
    }),
  }),
});

export const {
  useListAlbumsQuery,
  useGetAlbumQuery,
  useCreateAlbumMutation,
  useUpdateAlbumMutation,
  useArchiveAlbumMutation,
  useRestoreAlbumMutation,
  usePreflightPurgeAlbumMutation,
  usePurgeAlbumMutation,
  useBulkPurgeAlbumsMutation,
} = albumsApi;

export function extractAlbumErrorMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (
      data &&
      typeof data === "object" &&
      typeof data.message === "string" &&
      data.message.length > 0
    ) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}

export type AlbumSongDependencyDetails = {
  livingSongCount?: number;
  associatedTrackCount?: number;
  referencingSongs?: Array<{ trackId: string; title: string | null; status: string }>;
  songLibraryPath?: string;
};

export function extractAlbumSongDependencyDetails(
  err: unknown,
): AlbumSongDependencyDetails | null {
  if (!err || typeof err !== "object") return null;
  const data = (err as { data?: { details?: AlbumSongDependencyDetails } }).data;
  const details = data?.details;
  if (!details || typeof details !== "object") return null;
  if (
    typeof details.livingSongCount !== "number" &&
    typeof details.associatedTrackCount !== "number"
  ) {
    return null;
  }
  return details;
}
